import type { Ref } from 'vue'
import {
  buildDraftOccurrencesFromTask,
  scheduleApi
} from '@/api/schedule'
import { bellScheduleApi } from '@/api/bell-schedule'
import { HttpError } from '@/api/request'
import { markErrorSilent } from '@/utils/error-handler'
import { logger } from '@/utils/logger'
import { didScheduleStartupErrorRefresh } from '@/utils/scheduleStartupError'
import type {
  BellSchedule,
  RelaxationSession,
  ScheduleOccurrenceDraft,
  ScheduleRunDetail,
  ScheduleSnapshotDetail,
  ScheduleSnapshotSummary,
  ScheduleTask,
  ScheduleTaskDetail,
  ScheduleTaskUpsertRequest,
  TeacherSchedule
} from '@/types'
import { pickDefaultSnapshot, sortSnapshotsNewestFirst } from './snapshotHelpers'

type RefreshTaskOptions = {
  preferLatestGenerated?: boolean
}

type DraftComposable = {
  loadActiveSnapshot: (snapshotId: number) => Promise<void>
  hydrateDraftFromResults: (task: ScheduleTaskDetail) => Promise<void>
  applyDraftOccurrences: (
    occurrences: ScheduleOccurrenceDraft[],
    snapshot: ScheduleSnapshotDetail | null
  ) => void
}

type TaskCrudDeps = {
  tasks: Ref<ScheduleTask[]>
  currentTask: Ref<ScheduleTaskDetail | null>
  latestRun: Ref<ScheduleRunDetail | null>
  latestRelaxationSession: Ref<RelaxationSession | null>
  snapshots: Ref<ScheduleSnapshotSummary[]>
  activeSnapshot: Ref<ScheduleSnapshotDetail | null>
  draftOccurrences: Ref<ScheduleOccurrenceDraft[]>
  baselineDraftOccurrences: Ref<ScheduleOccurrenceDraft[]>
  selectedClassId: Ref<number | null>
  selectedSubgroupId: Ref<number | null>
  selectedTeacherId: Ref<number | null>
  teacherScheduleCache: Ref<Record<number, TeacherSchedule | null>>
  currentView: Ref<'tasks' | 'detail'>
  pendingAutoOpenTaskId: Ref<number | null>
  loading: Ref<boolean>
  detailLoading: Ref<boolean>
  saving: Ref<boolean>
  error: Ref<Error | null>
  detailError: Ref<Error | null>
  linkedBellSchedule: Ref<BellSchedule | null>
  draftComposable: DraftComposable
  ensureSelections: () => void
}

/**
 * 任务列表/详情 CRUD 与刷新。逻辑从 schedule store 原样迁出。
 */
export function useScheduleTaskCrud(deps: TaskCrudDeps): {
  clearPendingAutoOpen: () => void
  fetchTasks: (silent?: boolean) => Promise<void>
  fetchLinkedBellSchedule: () => Promise<void>
  backToTasks: () => void
  refreshCurrentTask: (taskId?: number, options?: RefreshTaskOptions) => Promise<void>
  selectTask: (task: ScheduleTask, options?: RefreshTaskOptions) => Promise<void>
  tryAutoOpenCompletedTask: (taskId: number) => Promise<void>
  createTask: (
    payload: ScheduleTaskUpsertRequest,
    startImmediately?: boolean,
    options?: { navigateToTaskList?: boolean }
  ) => Promise<ScheduleTaskDetail | undefined>
  updateTask: (
    id: number,
    payload: ScheduleTaskUpsertRequest,
    startImmediately?: boolean,
    options?: { navigateToTaskList?: boolean }
  ) => Promise<ScheduleTaskDetail | undefined>
  deleteTask: (taskId: number) => Promise<void>
  copyTask: (taskId: number) => Promise<void>
  refreshRelaxationSession: (taskId?: number) => Promise<void>
  setLatestRelaxationSession: (session: RelaxationSession | null) => void
  acknowledgeTaskOutcome: (taskId: number) => void
} {
  const {
    tasks,
    currentTask,
    latestRun,
    latestRelaxationSession,
    snapshots,
    activeSnapshot,
    draftOccurrences,
    baselineDraftOccurrences,
    selectedClassId,
    selectedSubgroupId,
    selectedTeacherId,
    teacherScheduleCache,
    currentView,
    pendingAutoOpenTaskId,
    loading,
    detailLoading,
    saving,
    error,
    detailError,
    linkedBellSchedule,
    draftComposable,
    ensureSelections
  } = deps

  function clearPendingAutoOpen(): void {
    pendingAutoOpenTaskId.value = null
  }

  /**
   * 加载任务列表
   * @param silent 静默刷新，不触发 loading 状态（用于事件流推送、增删改后的自动刷新）
   */
  async function fetchTasks(silent = false): Promise<void> {
    if (!silent) {
      loading.value = true
    }
    error.value = null

    try {
      const response = await scheduleApi.listTasks()
      tasks.value = response.items
    } catch (cause) {
      error.value = cause instanceof Error ? cause : new Error(String(cause))
    } finally {
      if (!silent) {
        loading.value = false
      }
    }
  }

  /**
   * 加载当前任务绑定的作息方案（用于调课网格与设置页一致）。
   *
   * 后端正常状态下一个学期只会关联一份作息；这里仍按任务 bellScheduleId
   * 精确匹配，避免异常数据或并发刷新把其它方案静默写入当前详情。
   */
  async function fetchLinkedBellSchedule(): Promise<void> {
    const task = currentTask.value
    const semesterId = task?.semesterId
    if (!task || semesterId == null) {
      linkedBellSchedule.value = null
      return
    }

    const taskId = task.id
    const bellScheduleId = task.bellScheduleId ?? null
    const isStillCurrentTask = (): boolean =>
      currentTask.value?.id === taskId
      && currentTask.value?.semesterId === semesterId
      && (currentTask.value?.bellScheduleId ?? null) === bellScheduleId
    const hasConfirmedCurrentSchedule = (): boolean => {
      const loaded = linkedBellSchedule.value
      if (!loaded) return false
      return bellScheduleId != null
        ? loaded.id === bellScheduleId
        : loaded.semesterId === semesterId
    }

    try {
      const response = await bellScheduleApi.listBellSchedules(semesterId)
      if (!isStillCurrentTask()) {
        return
      }
      const matched = bellScheduleId == null
        ? response.data[0] ?? null
        : response.data.find((item) => item.id === bellScheduleId) ?? null
      if (matched) {
        linkedBellSchedule.value = matched
        return
      }

      // 同一任务的窗口聚焦刷新若返回短暂空列表，保留已确认过的正确作息，
      // 不让整张网格在一次刷新中无声翻成“全部不可用”。
      if (!hasConfirmedCurrentSchedule()) {
        linkedBellSchedule.value = null
      }
      logger.warn('[ScheduleTaskCrud] 未找到任务绑定的作息方案', {
        taskId,
        semesterId,
        bellScheduleId
      })
    } catch (caughtError) {
      if (isStillCurrentTask()) {
        const preserved = hasConfirmedCurrentSchedule()
        if (!preserved) {
          linkedBellSchedule.value = null
        }
        logger.warn(
          preserved
            ? '[ScheduleTaskCrud] 刷新任务作息方案失败，保留当前已加载值'
            : '[ScheduleTaskCrud] 刷新任务作息方案失败，当前无可保留的已确认值',
          caughtError
        )
      }
    }
  }

  /**
   * 返回任务列表视图
   */
  function backToTasks(): void {
    currentView.value = 'tasks'
    currentTask.value = null
    linkedBellSchedule.value = null
    latestRun.value = null
    snapshots.value = []
    activeSnapshot.value = null
    draftOccurrences.value = []
    baselineDraftOccurrences.value = []
    selectedClassId.value = null
    selectedSubgroupId.value = null
    selectedTeacherId.value = null
    teacherScheduleCache.value = {}
    detailError.value = null
  }

  /**
   * 刷新当前任务详情（加载快照、运行状态等）
   */
  async function refreshCurrentTask(
    taskId = currentTask.value?.id,
    options?: RefreshTaskOptions
  ): Promise<void> {
    if (!taskId) {
      return
    }

    detailLoading.value = true
    detailError.value = null
    teacherScheduleCache.value = {}

    try {
      const [task, latestTaskRun, snapshotList, relaxationSession] = await Promise.all([
        scheduleApi.getTask(taskId),
        scheduleApi.getLatestRun(taskId),
        scheduleApi.listSnapshots(taskId),
        scheduleApi.getLatestRelaxationSession(taskId)
      ])

      if (task.status === 'TASK_PROCESSING' && currentView.value === 'detail') {
        backToTasks()
        return
      }
      // 加载期间用户已回到列表（如被并发的 PROCESSING 刷新踢回）时放弃回写：
      // 详情 DTO 不含卡片权威字段（cardRuntime/cardOutcome），写回列表会把卡片
      // 降级为「未启动排课」，且会重设 currentTask 令 SSE 事件循环反复触发本刷新，
      // 与 fetchTasks 交替造成严格轮卡片闪烁。
      if (currentView.value !== 'detail') {
        return
      }

      currentTask.value = task
      await fetchLinkedBellSchedule()
      latestRun.value = latestTaskRun
      latestRelaxationSession.value = relaxationSession
      snapshots.value = sortSnapshotsNewestFirst(snapshotList)

      const defaultSnapshot = pickDefaultSnapshot(
        snapshots.value,
        options?.preferLatestGenerated === true
      )
      if (defaultSnapshot) {
        await draftComposable.loadActiveSnapshot(defaultSnapshot.id)
      } else if (latestTaskRun) {
        await draftComposable.hydrateDraftFromResults(task)
      } else {
        draftComposable.applyDraftOccurrences(buildDraftOccurrencesFromTask(task), null)
      }

      ensureSelections()
    } catch (cause) {
      detailError.value = cause instanceof Error ? cause : new Error(String(cause))
    } finally {
      detailLoading.value = false
    }
  }

  /**
   * 确认卡片结果横幅（点 x / 进详情 / 编辑）：先本地翻转确认标记，再持久化到后端（幂等）。
   * 持久化失败时回滚本地标记，避免横幅状态与后端不一致。
   */
  function acknowledgeTaskOutcome(taskId: number): void {
    const index = tasks.value.findIndex((item) => item.id === taskId)
    if (index === -1) {
      return
    }
    const current = tasks.value[index]
    if (!current.cardOutcome || current.cardOutcome.acknowledged) {
      return
    }
    const outcome = current.cardOutcome
    tasks.value[index] = {
      ...current,
      cardOutcome: { ...outcome, acknowledged: true }
    }
    void scheduleApi.acknowledgeOutcome(taskId).catch(() => {
      const revertIndex = tasks.value.findIndex((item) => item.id === taskId)
      if (revertIndex === -1) {
        return
      }
      const revertCurrent = tasks.value[revertIndex]
      if (!revertCurrent.cardOutcome || revertCurrent.cardOutcome.message !== outcome.message) {
        return
      }
      tasks.value[revertIndex] = {
        ...revertCurrent,
        cardOutcome: { ...revertCurrent.cardOutcome, acknowledged: false }
      }
    })
  }

  /**
   * 选择并加载任务详情
   */
  async function selectTask(task: ScheduleTask, options?: RefreshTaskOptions): Promise<void> {
    // 查看详情 / 自动进详情：视为已确认结果横幅
    acknowledgeTaskOutcome(task.id)
    currentView.value = 'detail'
    currentTask.value = null
    linkedBellSchedule.value = null
    selectedClassId.value = null
    selectedSubgroupId.value = null
    selectedTeacherId.value = null
    await refreshCurrentTask(task.id, options)
  }

  /**
   * 排课成功后自动进入详情（仅用户本次启动且仍在列表页时）
   */
  async function tryAutoOpenCompletedTask(taskId: number): Promise<void> {
    if (pendingAutoOpenTaskId.value !== taskId) {
      return
    }
    if (currentView.value !== 'tasks') {
      clearPendingAutoOpen()
      return
    }

    const task = tasks.value.find((item) => item.id === taskId)
    if (!task) {
      return
    }
    // 空闲且存在未确认的成功结果时才自动进详情（后端权威字段驱动）
    if (task.cardRuntime != null) {
      return
    }
    if (task.cardOutcome?.type !== 'succeeded' || task.cardOutcome.acknowledged) {
      clearPendingAutoOpen()
      return
    }

    clearPendingAutoOpen()
    await selectTask(task, { preferLatestGenerated: true })
  }

  /**
   * 创建新任务
   *
   * @param options.navigateToTaskList 默认 true；向导中途草稿保存时应为 false
   */
  async function createTask(
    payload: ScheduleTaskUpsertRequest,
    startImmediately = false,
    options?: { navigateToTaskList?: boolean }
  ): Promise<ScheduleTaskDetail | undefined> {
    if (saving.value) {
      throw new Error('正在保存排课任务，请稍候再试')
    }

    saving.value = true
    const navigateToTaskList = options?.navigateToTaskList ?? true

    try {
      const createdTask = await scheduleApi.createTask(payload)

      if (startImmediately) {
        const beforeStart = createdTask
        try {
          await scheduleApi.startRun(createdTask.id)
          pendingAutoOpenTaskId.value = createdTask.id
          await fetchTasks(true)
        } catch (err) {
          await fetchTasks(true)
          const refreshedTask = tasks.value.find((task) => task.id === createdTask.id)
          if (didScheduleStartupErrorRefresh(refreshedTask, beforeStart)) {
            // 拦截详情已持久化：不弹 Toast（静默），由调用方留在第 5 步集中展示。
            throw markErrorSilent(err)
          }
          if (err instanceof HttpError) {
            throw err
          }
          const message = err instanceof Error ? err.message : '自动排课启动失败'
          throw new Error(`排课任务已创建，但自动排课启动失败：${message}`, { cause: err })
        }
      } else {
        await fetchTasks(true)
      }

      if (navigateToTaskList) {
        backToTasks()
      }

      return createdTask
    } finally {
      saving.value = false
    }
  }

  /**
   * 更新任务
   *
   * @param options.navigateToTaskList 默认 true；草稿自动保存时应为 false，避免踢回列表
   */
  async function updateTask(
    id: number,
    payload: ScheduleTaskUpsertRequest,
    startImmediately = false,
    options?: { navigateToTaskList?: boolean }
  ): Promise<ScheduleTaskDetail | undefined> {
    if (saving.value) {
      throw new Error('正在保存排课任务，请稍候再试')
    }

    saving.value = true
    const navigateToTaskList = options?.navigateToTaskList ?? true

    try {
      const updatedTask = await scheduleApi.updateTask(id, payload)

      if (startImmediately) {
        const beforeStart = updatedTask
        try {
          await scheduleApi.startRun(updatedTask.id)
          pendingAutoOpenTaskId.value = updatedTask.id
          await fetchTasks(true)
        } catch (err) {
          await fetchTasks(true)
          const refreshedTask = tasks.value.find((task) => task.id === updatedTask.id)
          if (didScheduleStartupErrorRefresh(refreshedTask, beforeStart)) {
            // 拦截详情已持久化：不弹 Toast（静默），由调用方留在第 5 步集中展示。
            throw markErrorSilent(err)
          }
          if (err instanceof HttpError) {
            throw err
          }
          const message = err instanceof Error ? err.message : '自动排课启动失败'
          throw new Error(`排课任务已更新，但自动排课启动失败：${message}`, { cause: err })
        }
      } else {
        await fetchTasks(true)
      }

      if (navigateToTaskList) {
        backToTasks()
      } else if (currentTask.value?.id === id) {
        currentTask.value = updatedTask
      }

      return updatedTask
    } finally {
      saving.value = false
    }
  }

  /**
   * 删除任务
   */
  async function deleteTask(taskId: number): Promise<void> {
    saving.value = true

    try {
      await scheduleApi.removeTask(taskId)
      await fetchTasks(true)

      if (currentTask.value?.id === taskId) {
        backToTasks()
      }
    } finally {
      saving.value = false
    }
  }

  /**
   * 复制任务：后端创建草稿副本（含第 1~4 步全部配置，不含课表结果），
   * 成功后仅刷新任务列表，用户留在列表页。
   */
  async function copyTask(taskId: number): Promise<void> {
    if (saving.value) {
      throw new Error('正在保存排课任务，请稍候再试')
    }

    saving.value = true

    try {
      await scheduleApi.copyTask(taskId)
      await fetchTasks(true)
    } finally {
      saving.value = false
    }
  }

  async function refreshRelaxationSession(taskId = currentTask.value?.id): Promise<void> {
    if (!taskId) {
      return
    }
    latestRelaxationSession.value = await scheduleApi.getLatestRelaxationSession(taskId)
  }

  function setLatestRelaxationSession(session: RelaxationSession | null): void {
    latestRelaxationSession.value = session
  }

  return {
    clearPendingAutoOpen,
    fetchTasks,
    fetchLinkedBellSchedule,
    backToTasks,
    refreshCurrentTask,
    selectTask,
    tryAutoOpenCompletedTask,
    createTask,
    updateTask,
    deleteTask,
    copyTask,
    refreshRelaxationSession,
    setLatestRelaxationSession,
    acknowledgeTaskOutcome
  }
}
